import { api } from "./baseApi";
import type { ApiResponse } from "@/types";

export interface TopMovingItem {
  id: string;
  name: string;
  category: string;
  quantitySold: number;
  revenue: number;
  averageDailySales: number;
  currentStock: number;
  stockCoverageDays: number | null;
  periodDays: number;
  recommendedOrderQuantity: number;
  recommendedReorderDate: string | null;
  daysUntilStockout: number | null;
  leadTimeDays: number;
  coverageDays: number;
}

export const dashboardApi = api.injectEndpoints({
  endpoints: builder => ({
    // Get sales summary for date range
    getSalesSummary: builder.query<
      ApiResponse<{
        totalRevenue: number;
        totalTransactions: number;
        averageTransaction: number;
        totalItems: number;
      }>,
      { startDate?: string; endDate?: string; outletId?: string; storeId?: string }
    >({
      query: ({ startDate, endDate, outletId, storeId }) => ({
        url: "/reports/sales-summary",
        params: { startDate, endDate, outletId, storeId },
      }),
      providesTags: ["Report"],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    // Get low stock items
    getLowStockItems: builder.query<
      ApiResponse<
        Array<{
          id: string;
          name: string;
          currentStock: number;
          lowStockThreshold: number;
          category: string;
        }>
      >,
      { outletId?: string; storeId?: string }
    >({
      query: ({ outletId, storeId }) => ({
        url: "/items/low-stock",
        params: { outletId, storeId },
      }),
      providesTags: ["Item"],
      keepUnusedDataFor: 120, // Cache for 2 minutes
    }),

    // Get recent transactions
    getRecentTransactions: builder.query<
      ApiResponse<{
        transactions: Array<{
          id: string;
          transactionCode: string;
          customerName?: string;
          totalAmount: number;
          status: string;
          createdAt: string;
          itemCount: number;
          items?: Array<{
            itemName: string;
            quantity: number;
            price: number;
            total: number;
          }>;
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>,
      { limit?: number; outletId?: string; storeId?: string }
    >({
      query: ({ limit = 10, outletId, storeId }) => ({
        url: "/transactions",
        params: { limit, page: 1, outletId, storeId },
      }),
      transformResponse: (response: any) => {
        // The transactions endpoint returns paginated data: { transactions: [...], pagination: {...} }
        const transactions = response?.data?.transactions || [];
        const pagination = response?.data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 };

        // Ensure transactions is an array
        if (!Array.isArray(transactions)) {
          return {
            success: true,
            data: {
              transactions: [],
              pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
            },
          };
        }

        return {
          success: true,
          data: {
            transactions: transactions.map((t: any) => ({
              id: t.id,
              transactionCode: t.transactionCode,
              customerName: t.customer?.name,
              totalAmount: t.grandTotal,
              status: t.status,
              createdAt: t.createdAt,
              itemCount: t.items?.length || 0,
              items:
                t.items?.map((item: any) => ({
                  itemName: item.itemName,
                  quantity: item.quantity,
                  price: item.price,
                  total: item.total,
                })) || [],
            })),
            pagination,
          },
        };
      },
      providesTags: ["Transaction"],
      keepUnusedDataFor: 180, // Cache for 3 minutes
    }),

    // Get sales trend data
    getSalesTrend: builder.query<
      ApiResponse<
        Array<{
          date: string;
          amount: number;
          transactions: number;
        }>
      >,
      { days?: number; outletId?: string; storeId?: string }
    >({
      query: ({ days = 7, outletId, storeId }) => ({
        url: "/reports/sales-trend",
        params: { days, outletId, storeId },
      }),
      providesTags: ["Report"],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    // Get top customers
    getTopCustomers: builder.query<
      ApiResponse<
        Array<{
          id: string;
          name: string;
          totalSpent: number;
          transactionCount: number;
          lastPurchase: string;
        }>
      >,
      { limit?: number; storeId?: string }
    >({
      query: ({ limit = 5, storeId }) => ({
        url: "/customers/top",
        params: { limit, storeId },
      }),
      providesTags: ["Customer"],
      keepUnusedDataFor: 300,
    }),

    // Get pending transactions
    getPendingTransactions: builder.query<
      ApiResponse<
        Array<{
          id: string;
          transactionCode: string;
          customerName?: string;
          totalAmount: number;
          status: string;
          createdAt: string;
          itemCount: number;
        }>
      >,
      { outletId?: string } | void
    >({
      query: (params) => ({
        url: "/transactions",
        params: {
          status: "PENDING",
          limit: 10,
          ...(params?.outletId && { outletId: params.outletId }),
        },
      }),
      transformResponse: (response: any) => {
        // The transactions endpoint returns paginated data: { transactions: [...], pagination: {...} }
        const transactions = response?.data?.transactions || [];

        // Ensure transactions is an array
        if (!Array.isArray(transactions)) {
          console.warn("Expected pending transactions to be an array, got:", typeof transactions);
          return {
            success: true,
            data: [],
          };
        }

        return {
          success: true,
          data: transactions.map((t: any) => ({
            id: t.id,
            transactionCode: t.transactionCode,
            customerName: t.customer?.name,
            totalAmount: t.grandTotal,
            status: t.status,
            createdAt: t.createdAt,
            itemCount: t.items?.length || 0,
          })),
        };
      },
      providesTags: ["Transaction"],
      keepUnusedDataFor: 120,
    }),

    // Get top moving items
    getTopMovingItems: builder.query<
      ApiResponse<Array<TopMovingItem>>,
      { limit?: number; days?: number; outletId?: string; storeId?: string }
    >({
      query: ({ limit = 8, days = 30, outletId, storeId }) => ({
        url: "/reports/top-moving-items",
        params: { limit, days, outletId, storeId },
      }),
      providesTags: ["Report"],
      keepUnusedDataFor: 300,
    }),

    // Get expenses summary for dashboard
    getExpensesSummary: builder.query<
      ApiResponse<{
        totalExpenses: number;
        monthlyExpenses: number;
        pendingExpenses: number;
        summary: {
          totalAmount: number;
          totalCount: number;
          previousPeriodAmount: number;
          percentageChange: number;
        };
      }>,
      { period?: "day" | "week" | "month" | "year"; outletId?: string; storeId?: string }
    >({
      query: ({ period = "month", outletId, storeId } = {}) => ({
        url: "/expenses/stats",
        params: { period, outletId, storeId },
      }),
      providesTags: ["Expense"],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetSalesSummaryQuery,
  useGetLowStockItemsQuery,
  useGetRecentTransactionsQuery,
  useGetSalesTrendQuery,
  useGetTopCustomersQuery,
  useGetPendingTransactionsQuery,
  useGetTopMovingItemsQuery,
  useGetExpensesSummaryQuery,
} = dashboardApi;
